import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { CSSTransition } from 'react-transition-group';
import { CustomHtml } from '@rmc/webcx-web-libs';
import styles from './index.module.scss';
import {
	BANNER_TEMPLATE,
	CARD_TEMPLATE,
	CUSTOM_HTML_TEMPLATE,
} from '../../config';
import {
	BannerPayloadType,
	CampaignType,
	CardPayloadType,
	HTMLPayloadType,
} from '../../types/campaignsApiResponse';
import { Banner } from '../Banner';
import { Card } from '../Card';
import { TextPropsType } from '../Text';
import { ActionButtonPropsType } from '../ActionButton';
import { CampaignOnScroll } from './CampaignOnScroll';
import { CampaignOnInit } from './CampaignOnInit';
import { canUseScrollDown } from './scrollUtils';
import { useResponsivePosition } from '../../hooks/useResponsivePosition';
import { Handler, Iframe } from '../Iframe/Iframe';
import { ClickTypeEnum } from '../../types/clickType.d';
import { getType } from '../MainApp/utils';
import '@rmc/webcx-web-libs/dist-npm/styles.css';

const CAMPAIGN_CSS_TRANSITION_TIMEOUT = 800;

type ApiTrackingFunctionsType = {
	onImgLoad?: () => void;
};

type CloseAfterClickFunctionsType = {
	onClickLink: () => void;
};

export type ClickLinkDataCampaignType = {
	redirectHost: string;
	clientId: string;
	version: string;
	sessionId: string;
	rcxpv?: string;
	issueId?: string | null;
	screen?: string;
};

type RatTrackingFunctionsType = {
	clickLinkData: ClickLinkDataCampaignType;
	onDisplay: () => void;
	onClose: () => void;
};

export type CampaignPropsType = CampaignType &
	ApiTrackingFunctionsType &
	RatTrackingFunctionsType & { onClose: () => void } & {
		style?: React.CSSProperties;
	} & CloseAfterClickFunctionsType;

function getTemplate({
	templateId,
	campaignId,
	contentId,
	payload,
	clickLinkData,
	onClickLink,
	onImgLoad,
	onClose,
	style,
}: CampaignPropsType) {
	switch (templateId) {
		case BANNER_TEMPLATE: {
			const data = payload as BannerPayloadType;
			return (
				<Banner
					image={data.image}
					onClose={onClose}
					clickLinkData={{
						...clickLinkData,
						campaignId,
						contentId,
						action: getType(ClickTypeEnum.IMAGE),
					}}
					onClick={onClickLink}
					onImgLoad={onImgLoad}
				/>
			);
		}
		case CARD_TEMPLATE: {
			const data = payload as CardPayloadType;

			return (
				<Card
					image={data.image}
					actionButton={data.actionButton as ActionButtonPropsType}
					bodyText={data.bodyText as TextPropsType}
					onClose={onClose}
					clickLinkData={{
						...clickLinkData,
						campaignId,
						contentId,
						action: getType(ClickTypeEnum.BUTTON),
					}}
					onClick={onClickLink}
					onImgLoad={onImgLoad}
					style={style}
				/>
			);
		}
		case CUSTOM_HTML_TEMPLATE: {
			const data = payload as HTMLPayloadType;
			return (
				<CustomHtml
					element={data.component}
					clickLinkData={{
						...clickLinkData,
						campaignId,
						contentId,
						action: getType(ClickTypeEnum.LINK),
					}}
					onClick={onClickLink}
					onImgLoad={onImgLoad}
					onClose={onClose}
				/>
			);
		}
		default:
			return null;
	}
}

export function CampaignComponent(campaign: CampaignPropsType) {
	const frameRef = useRef<Handler>(null);
	const [isPopupOpen, togglePopupOpen] = useState(false);
	const { payload, onDisplay, onClose, options, onClickLink } = campaign;
	const { layout, offsetX, offsetY } = options.position;
	const { background, ...iframeStyle } = {
		background: undefined, // NOTE: it could be not defined for destructure, ts concern
		...payload.style,
	};

	const handleShow = useCallback(() => {
		togglePopupOpen(true);
		// So to start the calculation it's required to remove this property
		// NOTE: After it's possible to start recalculation
		frameRef?.current?.triggerIframeRecalculation?.();
		onDisplay();
	}, []);

	const handleClose = useCallback(() => {
		togglePopupOpen(false);
		onClose();
	}, []);

	const handleClick = useCallback(() => {
		togglePopupOpen(false);
		onClickLink();
	}, []);

	const {
		options: {
			timing: { type, activate },
			trigger,
		},
	} = campaign;

	const isNoTimingAndTrigger =
		!canUseScrollDown(type, activate) &&
		typeof trigger?.displayTimer !== 'number';

	useEffect(() => {
		if (isNoTimingAndTrigger) {
			togglePopupOpen(true);
		}
	}, []);

	const { responsivePositionStyle, recalcPosition } = useResponsivePosition({
		layout,
		offsetX,
		offsetY,
	});

	const template = useMemo(
		() =>
			getTemplate({
				...campaign,
				onClose: handleClose,
				onClickLink: handleClick,
			}),
		[campaign, handleClose]
	);

	if (!template) {
		return null;
	}

	const popup = (
		<CSSTransition
			in={isPopupOpen}
			timeout={CAMPAIGN_CSS_TRANSITION_TIMEOUT}
			classNames={{
				enter: styles['push-up'],
				exit: styles['push-down'],
			}}
			unmountOnExit
		>
			<section className={styles.campaign} style={responsivePositionStyle}>
				<Iframe
					ref={frameRef}
					onIframeResize={recalcPosition}
					style={{
						...iframeStyle,
						pointerEvents: 'initial',
					}}
				>
					{template}
				</Iframe>
			</section>
		</CSSTransition>
	);

	const output = canUseScrollDown(type, activate) ? (
		<CampaignOnScroll
			displayTimer={trigger?.displayTimer || null}
			onScrollMatch={handleShow}
			scrollPercentage={activate}
		>
			{popup}
		</CampaignOnScroll>
	) : (
		<CampaignOnInit
			displayTimer={trigger?.displayTimer || null}
			onMount={handleShow}
		>
			{popup}
		</CampaignOnInit>
	);

	return output;
}

export const Campaign = memo(CampaignComponent);
